import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {supabase} from '../../api/supabase';
import {UserProfile} from './matchSlice';
import {validateRoomJoin} from '../../utils/roomValidation';

// Define types
export interface Room {
  id: string;
  created_at: string;
  chooser_id: string;
  status: string;
  timer_started_at: string | null;
  selected_participant_id: string | null;
  expires_at: string;
  max_candidates: number;
}

export interface RoomParticipant {
  id: string;
  room_id: string;
  user_id: string;
  response: 'yes' | 'no' | 'undecided';
  joined_at: string;
  is_room_creator: boolean;
  profiles?: UserProfile;
}

interface RoomState {
  currentRoom: Room | null;
  participants: RoomParticipant[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: RoomState = {
  currentRoom: null,
  participants: [],
  status: 'idle',
  error: null,
};

// Async thunks
export const createRoom = createAsyncThunk(
  'room/createRoom',
  async (_, {rejectWithValue, getState}) => {
    try {
      const {auth} = getState() as {auth: {user: {id: string}}};

      if (!auth.user) {
        return rejectWithValue('User not authenticated');
      }

      // Insert a new room record
      const {data: roomDataArray, error: roomError} = await supabase // Renamed to roomDataArray for clarity
        .from('rooms')
        .insert([
          {
            chooser_id: auth.user.id,
            status: 'active',
            expires_at: new Date(Date.now() + 3 * 60 * 1000).toISOString(),
            max_candidates: 4,
            timer_started_at: null, // <<< --- ADD THIS LINE ---
          },
        ])
        .select(); // Selects all columns of the inserted room

      if (roomError) {
        return rejectWithValue(roomError.message);
      }

      // Ensure roomDataArray is not null and has elements before accessing roomDataArray[0]
      if (!roomDataArray || roomDataArray.length === 0) {
        return rejectWithValue(
          'Failed to create room or retrieve it after creation',
        );
      }

      const newRoom = roomDataArray[0]; // The newly created room object

      // Also add creator as a participant
      const {data: participantData, error: participantError} = await supabase
        .from('room_participants')
        .insert([
          {
            room_id: newRoom.id,
            user_id: auth.user.id,
            response: 'yes',
            is_room_creator: true,
          },
        ])
        .select('*, profiles:user_id(id, username, full_name, photos)');

      if (participantError) {
        // Proceed even if participant creation fails for some reason, room is still made
      }

      return {
        room: newRoom, // newRoom should now reliably have timer_started_at: null
        participants: participantData || [],
      };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create room');
    }
  },
);

export const joinRoom = createAsyncThunk(
  'room/joinRoom',
  async (_, {rejectWithValue, getState}) => {
    try {
      const {auth} = getState() as {auth: {user: {id: string}}};

      if (!auth.user) {
        return rejectWithValue('User not authenticated');
      }
      const userId = auth.user.id;

      // Fetch available rooms
      const {data: rooms, error: roomsError} = await supabase
        .from('rooms')
        .select('*')
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString())
        .neq('chooser_id', userId)
        .order('created_at', {ascending: true});

      if (roomsError) {
        return rejectWithValue(roomsError.message);
      }

      if (!rooms?.length) {
        return rejectWithValue('No active rooms available');
      }

      let validationFailures = 0;
      let otherFailures = 0;

      // Loop through rooms to find a suitable one
      for (const room of rooms) {
        // Check if already a participant - improve error handling
        const {data: existing, error: checkError} = await supabase
          .from('room_participants')
          .select('*')
          .eq('room_id', room.id)
          .eq('user_id', userId)
          .maybeSingle(); // Use maybeSingle to handle no results more gracefully

        // Handle database errors more robustly
        if (checkError && checkError.code !== 'PGRST116') {
          otherFailures++;
          continue; // Skip to next room on error checking participation
        }

        // If already a participant, return existing data instead of skipping
        if (existing) {
          return {room, participant: existing};
        }

        // Check if room is already at capacity (max_candidates)
        const {data: participantCount, error: countError} = await supabase
          .from('room_participants')
          .select('id, is_room_creator')
          .eq('room_id', room.id);

        if (countError) {
          otherFailures++;
          continue; // Skip to next room on error checking participant count
        }

        // Count non-chooser participants (candidates)
        const candidateCount = participantCount
          ? participantCount.filter(p => !p.is_room_creator).length
          : 0;

        // If room already has 3 candidates, skip to next room
        if (candidateCount >= 3) {
          continue; // Room is full, try next room
        }

        // --- Validation step ---
        const validationResult = await validateRoomJoin(
          room.chooser_id,
          userId,
        );

        if (!validationResult.isValid) {
          validationFailures++;
          continue; // Skip this room if validation fails
        }

        // Try to insert participant with better error handling
        const {data: participant, error: joinError} = await supabase
          .from('room_participants')
          .insert([
            {
              room_id: room.id,
              user_id: userId,
              response: 'yes',
              is_room_creator: false,
            },
          ])
          .select('*, profiles:user_id(id, username, full_name, photos)') // Ensure profiles are selected on join
          .single();

        // Handle specific constraint errors
        if (joinError) {
          if (joinError.code === '23505') {
            // Unique constraint violation
            // Fetch existing participant data
            const {data: existingParticipant, error: fetchError} =
              await supabase
                .from('room_participants')
                .select('*, profiles:user_id(id, username, full_name, photos)')
                .eq('room_id', room.id)
                .eq('user_id', userId)
                .single();

            if (!fetchError && existingParticipant) {
              return {room, participant: existingParticipant};
            }
          }

          otherFailures++;
          continue; // Skip to next room on join error
        }

        // Successfully joined
        return {room, participant};
      }

      // If loop completes without finding a suitable room

      // Important: Use the EXACT SAME rejection message as when no rooms are available
      // This ensures the UI handles it the same way
      return rejectWithValue('No active rooms available');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to join room');
    }
  },
);

export const updateResponse = createAsyncThunk(
  'room/updateResponse',
  async (
    {
      roomId,
      response,
      userId,
    }: {roomId: string; response: 'yes' | 'no'; userId: string},
    {rejectWithValue},
  ) => {
    try {
      // Update participant's response
      const {data, error} = await supabase
        .from('room_participants')
        .update({response})
        .eq('room_id', roomId)
        .eq('user_id', userId)
        .select('*, profiles:user_id(id, username, full_name, photos)');

      if (error) {
        return rejectWithValue(error.message);
      }

      return data?.[0] || null;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update response');
    }
  },
);

export const startRoomTimer = createAsyncThunk(
  'room/startRoomTimer',
  async (roomId: string, {rejectWithValue}) => {
    try {
      // Only start the timer if it hasn't been started yet
      const {data: roomData, error: roomError} = await supabase
        .from('rooms')
        .select('timer_started_at')
        .eq('id', roomId)
        .single();

      if (roomError) {
        return rejectWithValue(roomError.message);
      }

      // If timer already started, just return the current room
      if (roomData.timer_started_at) {
        return roomData;
      }

      // Update the timer_started_at field to current time
      const {data, error} = await supabase
        .from('rooms')
        .update({timer_started_at: new Date().toISOString()})
        .eq('id', roomId)
        .select()
        .single();

      if (error) {
        return rejectWithValue(error.message);
      }

      return data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to start room timer');
    }
  },
);

export const closeRoomAndRemoveParticipants = createAsyncThunk(
  'room/closeRoomAndRemoveParticipants',
  async (roomId: string, {rejectWithValue}) => {
    try {
      // Update room status to completed
      const {error: updateError} = await supabase
        .from('rooms')
        .update({status: 'completed'})
        .eq('id', roomId);

      if (updateError) {
        return rejectWithValue(updateError.message);
      }

      // Optional: Consider if participants should still be removed or handled differently
      // when status is 'completed' vs 'closed'. Current logic removes them.
      const {error: deleteError} = await supabase
        .from('room_participants')
        .delete()
        .eq('room_id', roomId);

      if (deleteError) {
        // Log error but maybe don't reject, as room status update succeeded?
      }

      return {success: true, roomId};
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to close room');
    }
  },
);

export const updateSingleParticipant = createAsyncThunk(
  'room/updateSingleParticipant',
  async (
    {roomId, userId}: {roomId: string; userId: string},
    {rejectWithValue},
  ) => {
    try {
      // Fast query to get just the updated participant
      const {data: participant, error} = await supabase
        .from('room_participants')
        .select(
          `
          *,
          profiles:user_id (
            id,
            username,
            full_name,
            photos
          )
        `,
        )
        .eq('room_id', roomId)
        .eq('user_id', userId)
        .single();

      if (error) {
        return rejectWithValue(error.message);
      }

      return participant;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update single participant',
      );
    }
  },
);

export const getRoomParticipants = createAsyncThunk(
  'room/getParticipants',
  async (roomId: string, {rejectWithValue}) => {
    try {
      // Single optimized query with join to get all data at once
      const {data: participants, error: participantsError} = await supabase
        .from('room_participants')
        .select(
          `
          *,
          profiles:user_id (
            id,
            username,
            full_name,
            photos
          )
        `,
        )
        .eq('room_id', roomId)
        .order('joined_at', {ascending: true});

      if (participantsError) {
        return rejectWithValue(participantsError.message);
      }

      if (!participants || participants.length === 0) {
        return [];
      }

      // Get room info for chooser_id (separate small query)
      const {data: room, error: roomError} = await supabase
        .from('rooms')
        .select('chooser_id')
        .eq('id', roomId)
        .single();

      if (roomError) {
        return rejectWithValue(roomError.message);
      }

      // Process participants with chooser info
      const participantsWithProfiles = participants.map(participant => {
        // Set is_room_creator if not already set
        if (
          participant.is_room_creator === undefined ||
          participant.is_room_creator === null
        ) {
          participant.is_room_creator = participant.user_id === room.chooser_id;
        }

        return {
          ...participant,
          profiles: participant.profiles || null,
        };
      });

      return participantsWithProfiles;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to get room participants',
      );
    }
  },
);

// Slice
const roomSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    setParticipants: (state, action: PayloadAction<RoomParticipant[]>) => {
      state.participants = action.payload;
    },
    clearRoom: state => {
      state.currentRoom = null;
      state.participants = [];
      state.status = 'idle';
      state.error = null;
    },
    updateCurrentRoomData: (state, action: PayloadAction<Partial<Room>>) => {
      if (state.currentRoom && action.payload.id === state.currentRoom.id) {
        (Object.keys(action.payload) as Array<keyof Room>).forEach(key => {
          if (action.payload[key] !== undefined) {
            (state.currentRoom as any)[key] = action.payload[key];
          }
        });
      } else if (
        !state.currentRoom &&
        action.payload.id &&
        action.payload.status
      ) {
        state.currentRoom = action.payload as Room;
        state.status = 'succeeded';
      }
    },
  },
  extraReducers: builder => {
    builder
      // Create Room
      .addCase(createRoom.pending, state => {
        state.status = 'loading';
        state.error = null;
        state.currentRoom = null;
        state.participants = [];
      })
      .addCase(createRoom.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentRoom = action.payload.room;
        if (state.currentRoom) {
          state.currentRoom.timer_started_at = null;
        }
        state.participants = action.payload.participants;
        state.error = null;
      })
      .addCase(createRoom.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        state.currentRoom = null;
        state.participants = [];
      })

      // Join Room (ensure it clears currentRoom on failure too)
      .addCase(joinRoom.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(joinRoom.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentRoom = action.payload.room;
        if (action.payload.participant) {
          const newParticipant = action.payload.participant;
          const existingParticipantIndex = state.participants.findIndex(
            p => p.id === newParticipant.id,
          );
          if (existingParticipantIndex === -1) {
            state.participants = [...state.participants, newParticipant];
          } else {
            state.participants[existingParticipantIndex] = newParticipant;
          }
        }
        state.error = null;
      })
      .addCase(joinRoom.rejected, (state, action) => {
        state.status = 'failed';
        if (
          action.payload === 'No active rooms available' ||
          action.payload === 'Could not join any available rooms'
        ) {
          state.error = action.payload as string;
        } else {
          state.error =
            (action.payload as string) ||
            action.error.message ||
            'Failed to join room';
        }
        state.currentRoom = null;
        state.participants = [];
      })

      // Update Response
      .addCase(updateResponse.pending, (state, action) => {
        // Optimistic update: immediately update local state for instant UI feedback
        const {roomId, response, userId} = action.meta.arg;
        const participant = state.participants.find(
          p => p.room_id === roomId && p.user_id === userId,
        );
        if (participant) {
          participant.response = response;
        }
      })
      .addCase(updateResponse.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedParticipant = action.payload;
          const index = state.participants.findIndex(
            p => p.id === updatedParticipant.id,
          );
          if (index !== -1) {
            state.participants[index] = updatedParticipant;
          }
        }
      })
      .addCase(updateResponse.rejected, (state, action) => {
        // Revert optimistic update on failure by refetching participants
        // Note: In practice, you might want to revert to the previous state
      })

      // Start Room Timer
      .addCase(startRoomTimer.fulfilled, (state, action) => {
        if (state.currentRoom && action.payload?.timer_started_at) {
          // Check action.payload too
          state.currentRoom.timer_started_at = action.payload.timer_started_at;
        }
      })
      .addCase(startRoomTimer.rejected, (state, action) => {
        // Optionally set an error state or handle UI feedback
      })

      // Close Room (now setting to completed)
      .addCase(closeRoomAndRemoveParticipants.fulfilled, state => {
        if (state.currentRoom) {
          state.currentRoom.status = 'completed';
          // Consider if timer_started_at should be nulled here if you want to be super explicit
          // state.currentRoom.timer_started_at = null;
        }
        // Participants and currentRoom itself will be cleared by clearRoom on unmount/navigation.
      })
      .addCase(closeRoomAndRemoveParticipants.rejected, (state, action) => {
        // Optionally set an error state
      })

      // Update Single Participant (for fast balloon popping updates)
      .addCase(updateSingleParticipant.fulfilled, (state, action) => {
        if (action.payload && state.currentRoom) {
          const updatedParticipant = action.payload;
          const index = state.participants.findIndex(
            p => p.id === updatedParticipant.id,
          );
          if (index !== -1) {
            state.participants[index] = updatedParticipant;
          }
        }
      })

      // Get Room Participants
      .addCase(getRoomParticipants.fulfilled, (state, action) => {
        if (
          state.currentRoom &&
          action.payload.length > 0 &&
          action.payload[0].room_id === state.currentRoom.id
        ) {
          state.participants = action.payload;
        }
      });
  },
});

export const {setParticipants, clearRoom, updateCurrentRoomData} =
  roomSlice.actions;
export default roomSlice.reducer;
